<template>
  <div>
    <div class="d-flex flex-column">
      <div
        v-for="extension of extensions.filter(ex => !ex.used && !ex.discarded && ex.value)"
        :key="extension.valueId"
        class="d-flex flex-row"
      >
        <ArticlePartRemoveDialog
          :content="extension.value"
          lang-path="article.extendWithAi.discard"
          class="mt-2"
          :disabled="disabled"
          @removePart="discardExtension(extension)"
        />
        <v-tooltip top>
          {{ $t(`article.extendWithAi.${langPath}.use`) }}
          <v-icon
            class="ml-2"
            dark
          >
            playlist_add
          </v-icon>
          <template #activator="{ on }">
            <div
              class="extensionSheet"
              @click="useExtension(extension)"
              v-on="on"
            >
              <div
                class="pa-2"
                v-html="extension.value"
              />
            </div>
          </template>
        </v-tooltip>
      </div>
      <div
        class="extend-row"
        :class="{ 'extend-row--stack': $vuetify.breakpoint.smAndDown }"
      >
        <v-textarea
          v-if="!forcePrompt"
          v-model="prompt"
          auto-grow
          class="extend-row__prompt"
          dense
          :disabled="loading || disabled"
          :hint="prompt ? $t(`article.extendWithAi.${langPath}.prompt`) : undefined"
          hide-details="auto"
          :loading="loading"
          outlined
          :placeholder="$t(`article.extendWithAi.${langPath}.prompt`)"
          :rows="1"
        />
        <v-btn
          color="accent"
          outlined
          :elevation="prompt ? 4 : 0"
          :disabled="wordsGeneratedTotal >= wordsAvailable || disabled"
          :loading="loading"
          :class="btnClass"
          @click="onInput"
        >
          <v-icon class="mr-2">
            send
          </v-icon>
          {{
            $t(`article.extendWithAi.${langPath}.run`) + ` (${Math.max(wordsAvailable - wordsGeneratedTotal, 0)}/${wordsAvailable})`
          }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

    const OPENAI_ARTICLE_PART_EXTENSION_LIMIT = 500;

    import Vue, {PropType} from 'vue';
    import {Functions} from "@/plugins/Functions";
    import {Firestore} from "@/plugins/Firestore";
    import {Article, ArticleContentKey} from "@/model/Article";
    import {sanitizeExtendedParagraphHtml} from '@/service/articlePartExtendSanitize';
    import {sanitizeFragmentHtml} from '@/utils/sanitizeHtml';
    import ArticlePartRemoveDialog from "@/components/article/ArticlePartRemoveDialog.vue";
    import {OpenAiExpandPartType, OpenAiExtendPartData, OpenAiExtendPartDataWithId, OpenAiExtendPartRequest} from "@/model/OpenAI";
    import {captureException} from "@/plugins/Sentry";
    import { Toast } from '@/model/Toast';

    export default Vue.extend({
        name: "ArticlePartExtend",
        components: {ArticlePartRemoveDialog},
        props: {
            part: {
                type: String as PropType<ArticleContentKey | 'question'>,
                default: ''
            },
            uid: {
                type: Number,
                default: 0
            },
            forcePrompt: {
                type: String,
                default: ''
            },
            hideExtensions: {
                type: Boolean,
                default: false
            },
            loading: {
                type: Boolean,
                default: false
            },
            wordsGeneratedElsewhere: {
                type: Number,
                default: 0
            },
            watchForExtensionValue: {
                type: String,
                default: ''
            },
            langPath: {
                type: String as PropType<'heading' | 'paragraph' | 'question' | 'answer'>,
                default: 'paragraph'
            },
            languageName: {
                type: String,
                default: 'English'
            },
            type: {
                type: String as PropType<OpenAiExpandPartType>,
                default: OpenAiExpandPartType.FROM_USER_INPUT
            },
            disabled: {
                type: Boolean,
                default: false
            },
        },
        data: () => ({
            prompt: '',
            extensions: [] as OpenAiExtendPartDataWithId[],
            wordsAvailable: OPENAI_ARTICLE_PART_EXTENSION_LIMIT
        }),
        computed: {
            articleId: function (): string {
                return this.$store.getters['article'].id;
            },
            article: function (): Article {
                return this.$store.getters.article;
            },
            wordsGenerated: function (): number {
                const count = this.extensions.reduce((acc, curr) => acc + curr.value.split(' ').length, 0);
                this.$emit('wordsGenerated', count);
                return count;
            },
            wordsGeneratedTotal: function (): number {
                return this.wordsGenerated + this.wordsGeneratedElsewhere;
            },
            btnClass: function (): string {
                const classes = ['extend-row__btn'];
                if (this.hideExtensions) {
                    classes.push('multi-line-button', 'heading-generate-btn');
                }
                return classes.join(' ');
            }
        },
        watch: {
            watchForExtensionValue: function (valueId) {
                if (valueId) {
                    this.watchForExtension(valueId);
                }
            },
        },
        created: function () {
            if (this.$store.getters.sharePreview || !this.$store.getters.authenticated) {
                return;
            }
            this.fetchExtensions();
        },
        methods: {
            fetchExtensions: function () {
                if (!this.hideExtensions) {
                    this.$emit('update:loading', true);
                    Firestore.getArticleOpenAiPartExtensions(this.articleId, this.part as ArticleContentKey, this.uid)
                        .then(extensions => {
                            // Stored values are model output; clean them before v-html (#30939).
                            this.extensions = extensions.map(extension => ({
                                ...extension,
                                value: sanitizeFragmentHtml(extension.value),
                            }));
                            this.$emit('update:loading', false);
                        });
                }
            },
            onInput: function () {
                if (!(this.forcePrompt || this.prompt)) {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'article.extendWithAi.fillPrompt'
                    } as Toast);
                    return;
                }
                this.$emit('update:loading', true);
                this.$store.dispatch('toasts/add', {
                    type: 'info',
                    title: 'article.extendWithAi.generating',
                    dismissible: false,
                } as Toast);
                Functions.OpenAIArticlePartExtend({
                    articleId: this.articleId,
                    part: this.part,
                    uid: this.uid,
                    type: this.type,
                    data: {
                        ...(this.type === OpenAiExpandPartType.FROM_USER_INPUT ? {userInput: this.prompt} : {}),
                        ...(this.type === OpenAiExpandPartType.FROM_HEADING ? {heading: this.forcePrompt} : {}),
                        ...(this.type === OpenAiExpandPartType.FROM_QUESTION ? {question: this.forcePrompt} : {}),
                    }
                } as OpenAiExtendPartRequest).then(response => {
                    this.$emit('generatingExtensionValueId', response.valueId);
                    if (!this.hideExtensions) {
                        this.watchForExtension(response.valueId);
                    }
                }).catch(error => {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'unexpectedError',
                    } as Toast);
                    captureException(error);
                    this.$emit('update:loading', false);
                });
            },
            watchForExtension: function (valueId: string) {
                const stopWatcher = Firestore.createArticleOpenAiPartExtensionWatcher(
                    this.articleId, this.part as ArticleContentKey, this.uid, valueId,
                    (data: OpenAiExtendPartData) => {
                        if (data.state !== "running") {
                            this.$emit('update:loading', false);
                            stopWatcher();
                        }
                        if (data.state === "done") {
                            this.prompt = '';
                        } else if (data.state === "failed") {
                            // keep the prompt so the user can try again
                            this.$store.dispatch('toasts/add', {
                                type: 'error',
                                title: 'unexpectedError',
                            } as Toast);
                        }
                        const value = sanitizeFragmentHtml(sanitizeExtendedParagraphHtml(data.value));
                        const localIndex = this.extensions.findIndex(ex => ex.valueId === valueId);
                        if (localIndex !== -1) {
                            this.extensions[localIndex].state = data.state;
                            this.extensions[localIndex].value = value;
                        } else {
                            this.extensions.push({
                                ...data,
                                valueId: valueId,
                                value
                            });
                        }
                    });
            },
            useExtension: function (extension: OpenAiExtendPartDataWithId) {
                this.$set(extension, 'used', true);
                this.$emit('append', extension.value);
                Firestore.setArticleOpenAiPartExtensionUsed(
                    this.articleId, this.part as ArticleContentKey, this.uid, extension.valueId, true
                );
            },
            discardExtension: function (extension: OpenAiExtendPartDataWithId) {
                this.$set(extension, 'discarded', true);
                Firestore.setArticleOpenAiPartExtensionDiscarded(
                    this.articleId, this.part as ArticleContentKey, this.uid, extension.valueId, true
                );
            }
        }
    });
</script>

<style scoped lang="sass">
$extend-control-height: 44px

.extend-row
    display: flex
    align-items: flex-start
    gap: 8px
    margin: 8px 0 4px

.extend-row--stack
    flex-direction: column
    align-items: stretch

.extend-row__prompt
    flex: 1 1 auto
    margin: 0 !important

    ::v-deep .v-input__control > .v-input__slot
        min-height: $extend-control-height !important
        margin-bottom: 0 !important

.extend-row__btn.v-btn
    height: $extend-control-height !important
    min-height: $extend-control-height !important
    margin: 0 !important
    flex: 0 0 auto
    align-self: flex-start

.extend-row--stack .extend-row__btn.v-btn
    align-self: stretch
    width: 100%

.extensionSheet

    margin-top: 8px

    &, div
        transition: 150ms
        cursor: pointer

    div
        border: 1px dashed var(--v-primary-base)

    &:hover
        padding-bottom: 8px

        div
            transition: 150ms
            margin-top: -8px
            // @extend .elevation-8
            box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12) !important
// end of extend
</style>
